import React from 'react';
import { useSWRConfig } from 'swr';
import useOwnerOrgId from '@/lib/hooks/useOwnerOrgId';
import deleteOpportunityConfidenceLevel from '@/services/opportunityConfidenceLevels/deleteOpportunityConfidenceLevel';
import postOpportunityConfidenceLevel from '@/services/opportunityConfidenceLevels/postOpportunityConfidenceLevel';
import putOpportunityConfidenceLevel from '@/services/opportunityConfidenceLevels/putOpportunityConfidenceLevel';
import type OpportunityConfidenceLevel from '@/types/api/OpportunityConfidenceLevel';
import type PostOpportunityConfidenceLevel from '@/types/api/PostOpportunityConfidenceLevel';
import type PutOpportunityConfidenceLevel from '@/types/api/PutOpportunityConfidenceLevel';
import addItemToListMutation from '@/utils/swr/addItemToListMutation';
import removeItemFromListMutation from '@/utils/swr/removeItemFromListMutation';
import updateItemInListMutation from '@/utils/swr/updateItemInListMutation';

export default function useConfidenceLevelServices() {
  const ownerOrgId = useOwnerOrgId();
  const { mutate } = useSWRConfig();

  const DATA_KEY = 'opportunityConfidenceLevels';
  const ID_KEY = 'confidenceLevelId';

  const removeConfidenceLevelFromList = React.useCallback(
    (confidenceLevelId: string) => {
      mutate(
        (key) =>
          typeof key === 'string' &&
          key.startsWith('opportunity-confidence-levels?'),
        (currentData) =>
          removeItemFromListMutation<
            typeof DATA_KEY,
            typeof ID_KEY,
            OpportunityConfidenceLevel
          >({
            dataKey: DATA_KEY,
            idKey: ID_KEY,
            itemId: confidenceLevelId,
            currentData,
          }),
        { revalidate: false },
      );
    },
    [mutate],
  );

  const addConfidenceLevelToList = React.useCallback(
    (confidenceLevelResponse: OpportunityConfidenceLevel) => {
      mutate(
        (key) =>
          typeof key === 'string' &&
          key.startsWith('opportunity-confidence-levels?'),
        (currentData) =>
          addItemToListMutation<
            typeof DATA_KEY,
            typeof ID_KEY,
            OpportunityConfidenceLevel
          >({
            dataKey: DATA_KEY,
            idKey: ID_KEY,
            currentData,
            item: confidenceLevelResponse,
          }),
        { revalidate: false },
      );
    },
    [mutate],
  );

  const postConfidenceLevel = async (
    values: PostOpportunityConfidenceLevel,
  ) => {
    const response = await postOpportunityConfidenceLevel(values);

    if (response) {
      const { opportunityConfidenceLevel } = response.data;
      addConfidenceLevelToList(opportunityConfidenceLevel);
    }
  };

  const updateConfidenceLevel = React.useCallback(
    async (
      confidenceLevelId: string,
      values: PutOpportunityConfidenceLevel,
    ) => {
      const updatedConfidenceLevel = await putOpportunityConfidenceLevel(
        confidenceLevelId,
        values,
      );

      if (!updatedConfidenceLevel) {
        return;
      }

      mutate(
        (key) =>
          typeof key === 'string' &&
          key.startsWith('opportunity-confidence-levels?'),
        (currentData) =>
          updateItemInListMutation<
            typeof DATA_KEY,
            typeof ID_KEY,
            OpportunityConfidenceLevel
          >({
            dataKey: DATA_KEY,
            idKey: ID_KEY,
            currentData,
            item: updatedConfidenceLevel.data
              .opportunityConfidenceLevel as OpportunityConfidenceLevel,
          }),
        { revalidate: false },
      );
    },
    [mutate],
  );

  const deleteConfidenceLevel = React.useCallback(
    async (confidenceLevelId: string) => {
      await deleteOpportunityConfidenceLevel(confidenceLevelId, ownerOrgId);

      removeConfidenceLevelFromList(confidenceLevelId);
    },
    [ownerOrgId],
  );

  return {
    postConfidenceLevel,
    updateConfidenceLevel,
    deleteConfidenceLevel,
  };
}
